import { useMemo, useState } from 'react'
import ReactECharts from 'echarts-for-react'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { mdiDelete, mdiGauge } from '@mdi/js'
import { $api } from '../../api/queryClient'
import { Can } from '../../contexts/PermissionsContext'
import { useTheme } from '../../contexts/ThemeContext'
import { useFormatters } from '../../lib/format'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/checkbox'
import { Input } from '../ui/input'
import { AppIcon } from '../ui/icon'
import { cssToken } from '../benchmark/benchmark-utils'
import { chartPoints, sortReadings } from './meter-consumption'

const READINGS_PATH = '/api/v1/organizations/{orgCode}/assets/{assetUid}/meter-readings' as const
const VISIBLE_READINGS = 10
const CONSUMPTION_PATH = '/api/v1/organizations/{orgCode}/assets/{assetUid}/meter-consumption' as const

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

/**
 * A meter's readings (spec 2026-10-27-18, part B.1): the form that appends a
 * dated reading, the series, and the monthly consumption chart. A reading is
 * never edited in place: a correction is a new reading, or a deletion.
 */
export function MeterReadingsCard({ org, assetUid }: { org: string; assetUid: string }) {
  const { t } = useTranslation('assets')
  const { formatDate, formatNumber } = useFormatters()
  const queryClient = useQueryClient()
  const path = { orgCode: org, assetUid }
  const readings = $api.useQuery('get', READINGS_PATH, { params: { path, query: { limit: 100 } } }, { retry: false })
  const consumption = $api.useQuery('get', CONSUMPTION_PATH, { params: { path, query: { months: 24 } } }, { retry: false })
  const add = $api.useMutation('post', READINGS_PATH)
  const remove = $api.useMutation('delete', '/api/v1/organizations/{orgCode}/assets/{assetUid}/meter-readings/{readingUid}')

  const [value, setValue] = useState('')
  const [date, setDate] = useState(today())
  const [estimated, setEstimated] = useState(false)
  const [reset, setReset] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = () =>
    queryClient.invalidateQueries({
      predicate: (q) => q.queryKey[0] === 'get' && (q.queryKey[1] === READINGS_PATH || q.queryKey[1] === CONSUMPTION_PATH),
    })

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const n = Number(value)
    if (value.trim() === '' || !Number.isFinite(n) || !date) return
    setError(null)
    try {
      await add.mutateAsync({
        params: { path },
        body: { readings: [{ value: n, read_at: date, kind: estimated ? 'estimated' : 'actual', reset }] },
      })
      setValue('')
      setEstimated(false)
      setReset(false)
      await refresh()
    } catch (err) {
      const code = (err as { error?: { code?: string }; code?: string })?.error?.code ?? (err as { code?: string })?.code
      setError(
        code === 'reading_decreases'
          ? t('meter_readings.error_decreases')
          : code === 'reading_exists'
            ? t('meter_readings.error_exists')
            : code === 'reading_in_future'
              ? t('meter_readings.error_future')
              : t('meter_readings.error'),
      )
    }
  }

  const [showAll, setShowAll] = useState(false)
  const allRows = sortReadings(readings.data?.data ?? [])
  const rows = showAll ? allRows : allRows.slice(0, VISIBLE_READINGS)
  const unit = consumption.data?.unit ?? ''

  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid="meter-readings-card">
      <div className="mb-3 flex items-center gap-2">
        <AppIcon path={mdiGauge} size="sm" className="text-muted-foreground" />
        <h3 className="text-sm font-semibold">{t('meter_readings.title')}</h3>
      </div>

      <Can perm="assets.write">
        <form className="flex flex-wrap items-end gap-3" onSubmit={submit} data-testid="meter-reading-form">
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            {t('meter_readings.value')}
            <Input
              type="number"
              step="any"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="h-8 w-32"
              data-testid="meter-reading-value"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            {t('meter_readings.date')}
            <Input
              type="date"
              value={date}
              max={today()}
              onChange={(e) => setDate(e.target.value)}
              className="h-8 w-40"
              data-testid="meter-reading-date"
            />
          </label>
          <label className="flex items-center gap-2 text-xs">
            <Checkbox checked={estimated} onCheckedChange={(v) => setEstimated(v === true)} data-testid="meter-reading-estimated" />
            {t('meter_readings.estimated')}
          </label>
          <label className="flex items-center gap-2 text-xs">
            <Checkbox checked={reset} onCheckedChange={(v) => setReset(v === true)} data-testid="meter-reading-reset" />
            {t('meter_readings.reset')}
          </label>
          <Button type="submit" size="sm" disabled={add.isPending || value.trim() === ''} data-testid="meter-reading-submit">
            {t('meter_readings.add')}
          </Button>
        </form>
        {error && (
          <p className="mt-2 text-sm text-destructive" data-testid="meter-reading-error">
            {error}
          </p>
        )}
      </Can>

      <ConsumptionChart
        periods={consumption.data?.data ?? []}
        climateSensitive={!!consumption.data?.climate_sensitive}
        cooling={consumption.data?.end_use === 'cooling'}
        unit={unit}
      />

      <div className="mt-4">
        <h4 className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('meter_readings.series')}</h4>
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="meter-readings-empty">
            {t('meter_readings.empty')}
          </p>
        ) : (
          <ul className="divide-y text-sm" data-testid="meter-readings-list">
            {rows.map((r) => (
              <li key={r.uid} className="flex items-center gap-3 py-1.5" data-testid={`meter-reading-row-${r.uid}`}>
                <span className="w-28 text-muted-foreground tabular-nums">{formatDate(r.read_at, { timeZone: 'UTC' })}</span>
                <span className="flex-1 font-medium tabular-nums" data-testid="meter-reading-cell-value">
                  {formatNumber(r.value)} {unit}
                </span>
                {r.kind === 'estimated' && (
                  <span className="rounded bg-muted px-1.5 text-[11px] text-muted-foreground">{t('meter_readings.estimated')}</span>
                )}
                {r.reset && <span className="rounded bg-muted px-1.5 text-[11px] text-muted-foreground">{t('meter_readings.reset')}</span>}
                <Can perm="assets.write">
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-muted-foreground hover:text-destructive"
                    aria-label={t('meter_readings.delete')}
                    title={t('meter_readings.delete')}
                    onClick={async () => {
                      await remove.mutateAsync({ params: { path: { ...path, readingUid: r.uid } } })
                      await refresh()
                    }}
                    data-testid={`meter-reading-delete-${r.uid}`}
                  >
                    <AppIcon path={mdiDelete} size="xs" />
                  </Button>
                </Can>
              </li>
            ))}
          </ul>
        )}
        {allRows.length > VISIBLE_READINGS && (
          <Button variant="link" size="sm" className="px-0" onClick={() => setShowAll((v) => !v)} data-testid="meter-readings-toggle">
            {showAll ? t('meter_readings.show_less') : t('meter_readings.show_all', { count: allRows.length })}
          </Button>
        )}
      </div>
    </div>
  )
}

function ConsumptionChart({
  periods,
  climateSensitive,
  cooling,
  unit,
}: {
  periods: { period_start: string; consumption?: number | null; quality: string; heating_degree_days?: number | null; cooling_degree_days?: number | null }[]
  climateSensitive: boolean
  cooling: boolean
  unit: string
}) {
  const { t } = useTranslation('assets')
  useTheme()
  const [normalized, setNormalized] = useState(false)
  const points = useMemo(() => chartPoints(periods, normalized && climateSensitive, cooling), [periods, normalized, climateSensitive, cooling])

  const option = useMemo(() => {
    const bar = cssToken('--chart-1', '#2563eb')
    const line = cssToken('--muted-foreground', '#6b7280')
    const border = cssToken('--border', '#d1d5db')
    const foreground = cssToken('--foreground', '#0a0a0a')
    const max = Math.max(1, ...points.map((p) => Math.max(p.value ?? 0, p.lastYear ?? 0)))
    return {
      grid: { left: 48, right: 12, top: 24, bottom: 28 },
      tooltip: { trigger: 'axis' },
      legend: { top: 0, right: 0, textStyle: { color: foreground, fontSize: 11 } },
      xAxis: { type: 'category', data: points.map((p) => p.month), axisLabel: { color: line, fontSize: 10 } },
      yAxis: { type: 'value', axisLabel: { color: line, fontSize: 10 }, splitLine: { lineStyle: { color: border } } },
      series: [
        {
          name: t('meter_readings.chart_consumption'),
          type: 'bar',
          data: points.map((p) =>
            p.insufficient
              ? { value: max, itemStyle: { color: 'transparent', borderColor: border, decal: { symbol: 'rect', dashArrayX: [1, 0], dashArrayY: [2, 4], rotation: -Math.PI / 4, color: border } } }
              : { value: p.value, itemStyle: { color: bar } },
          ),
        },
        {
          name: t('meter_readings.chart_last_year'),
          type: 'line',
          data: points.map((p) => p.lastYear),
          symbolSize: 4,
          lineStyle: { color: line, type: 'dashed' },
          itemStyle: { color: line },
        },
      ],
      aria: { enabled: true, decal: { show: false } },
    }
  }, [points, t])

  if (periods.length === 0) {
    return (
      <p className="mt-4 text-sm text-muted-foreground" data-testid="meter-consumption-empty">
        {t('meter_readings.chart_empty')}
      </p>
    )
  }

  return (
    <div className="mt-4" data-testid="meter-consumption-chart">
      <div className="mb-1 flex items-center gap-3">
        <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t('meter_readings.chart_title')} {unit && `(${normalized && climateSensitive ? `${unit} / DJU` : unit})`}
        </h4>
        {climateSensitive && (
          <label className="ml-auto flex items-center gap-2 text-xs">
            <Checkbox checked={normalized} onCheckedChange={(v) => setNormalized(v === true)} data-testid="meter-consumption-normalized" />
            {t('meter_readings.chart_normalized')}
          </label>
        )}
      </div>
      <ReactECharts option={option} style={{ height: 220 }} notMerge />
      <p className="text-[11px] text-muted-foreground">{t('meter_readings.chart_hatched')}</p>
    </div>
  )
}
